import React, { useEffect, useMemo, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Divider,
  Table,
  TableBody,
  Alert,
  Button
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatAmountWithDecimals } from 'src/utils/formatAmout';
import StatusIcon from 'src/components/StatusIcon';
import momentLocalTimezone from 'src/utils/momentLocalTimezone';
import DetailRow from 'src/components/DetailRow';
import DocumentLink from 'src/components/DocumentLink/DocumentLink';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectAgentsList,
  selectInsurersList
} from 'src/store/selectors/catalogSelectors';
import {
  LOAD_CATALOG,
  loadAgentsList,
  loadInsurersList
} from 'src/store/actions/catalogActions';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import {
  createAgencyNameList,
  createInsurerNameList,
  getInsuranceStatus
} from 'src/utils/constants/insurance';
import Preloader from 'src/components/Preloader';
import { Edit } from 'react-feather';
import clsx from 'clsx';
import InsuranceDetailDialog from 'src/views/equipment/EquipmentDetail/InsuranceDetail/IsuranceDetailDialog';
import {
  updateInsurance,
  loadInsuranceDetail
} from 'src/store/actions/insuranceActions';
import useStyles from './styles';

const GeneralView = ({ insurance }) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const agencies = useSelector(selectAgentsList);
  const insurers = useSelector(selectInsurersList);
  const isLoadingCatalog = useSelector(
    isLoadingSelector([LOAD_CATALOG.action])
  );
  const [open, setOpen] = useState(false);

  const insurerNameList = useMemo(() => createInsurerNameList(insurers), [
    insurers
  ]);
  const agencyNameList = useMemo(() => createAgencyNameList(agencies), [
    agencies
  ]);

  const formatCurrency = useCallback(
    amount => {
      if (!amount) return t('COMMON.NO_INFORMATION');
      return `$${formatAmountWithDecimals(amount)}`;
    },
    [t]
  );

  const formatDate = useCallback(
    date => {
      if (!date) return t('COMMON.NO_INFORMATION');
      return momentLocalTimezone(date).format('DD/MM/YYYY');
    },
    [t]
  );

  const formatPercentage = useCallback(
    value => {
      if (!value) return t('COMMON.NO_INFORMATION');
      return `${value}%`;
    },
    [t]
  );

  const getInsurerName = useCallback(
    insurer => {
      if (!insurer) return t('COMMON.NO_INFORMATION');
      return (
        insurerNameList.find(insurerItem => insurerItem.key === insurer.id)
          ?.name || t('COMMON.NO_INFORMATION')
      );
    },
    [insurerNameList, t]
  );

  const getAgencyName = useCallback(
    agencyId => {
      if (!agencyId) return t('COMMON.NO_INFORMATION');
      return (
        agencyNameList.find(agency => agency.key === agencyId)?.name ||
        t('COMMON.NO_INFORMATION')
      );
    },
    [agencyNameList, t]
  );

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleEditInsurance = useCallback(
    values => {
      dispatch(
        dispatch(
          updateInsurance(
            insurance.policyId,
            values,
            () => {
              dispatch(loadInsuranceDetail(insurance.policyId));
            },
            handleClose
          )
        )
      );
    },
    [dispatch, insurance.policyId, handleClose]
  );
  useEffect(() => {
    dispatch(loadAgentsList());
    dispatch(loadInsurersList());
  }, [dispatch]);

  const handleClickOpen = useCallback(() => {
    setOpen(true);
  }, []);

  if (isLoadingCatalog) {
    return (
      <Box
        mt={3}
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <Preloader />
      </Box>
    );
  }

  if (!insurance) {
    return (
      <Box mt={3}>
        <Alert severity="info">
          <Typography variant="h6" color="textSecondary">
            {t('COMMON.NO_INFORMATION')}
          </Typography>
        </Alert>
      </Box>
    );
  }

  const PolicyInformationCard = () => (
    <Card variant="elevation">
      <CardHeader
        className={classes.header}
        title={
          <Typography className={classes.tableCell} component="h4" variant="h4">
            {t('INSURANCE_VIEW.POLICY_INFORMATION')}
          </Typography>
        }
      />
      <Divider className={classes.tableCell} />
      <CardContent className={classes.cardContent}>
        <Table>
          <TableBody>
            <DetailRow
              name={t('INSURANCE_VIEW.STATUS')}
              value={
                <StatusIcon
                  {...getInsuranceStatus(insurance.insuranceStatus, t)}
                />
              }
            />
            <DetailRow
              name={t('INSURANCE_VIEW.POLICY_NUMBER')}
              value={insurance.policyId || t('COMMON.NO_INFORMATION')}
            />
            <DetailRow
              name={t('INSURANCE_VIEW.INSURANCE')}
              value={getInsurerName(insurance.insurer)}
            />
            <DetailRow
              name={t('INSURANCE_VIEW.AGENCY')}
              value={getAgencyName(insurance.agencyId)}
            />
            <DetailRow
              name={t('INSURANCE_VIEW.COVERAGE_START')}
              value={formatDate(insurance.coverageStart)}
            />
            <DetailRow
              name={t('INSURANCE_VIEW.COVERAGE_END')}
              value={formatDate(insurance.coverageEnd)}
            />
          </TableBody>
          <Grid container direction="column" alignItems="stretch">
            <Grid item className={classes.buttons}>
              <Button
                onClick={handleClickOpen}
                variant="contained"
                color="primary"
                className={clsx(classes.label, classes.buttons)}
                startIcon={<Edit />}
              >
                {t('COMMON.EDIT')}
              </Button>
            </Grid>
          </Grid>
        </Table>
        <InsuranceDetailDialog
          open={open}
          handleClose={handleClose}
          insuranceData={insurance}
          submit={handleEditInsurance}
        />
      </CardContent>
    </Card>
  );

  const FinancialInformationCard = () => (
    <Card variant="elevation">
      <CardHeader
        className={classes.header}
        title={
          <Typography className={classes.tableCell} component="h4" variant="h4">
            {t('INSURANCE_VIEW.FINANCIAL_INFORMATION')}
          </Typography>
        }
      />
      <Divider className={classes.tableCell} />
      <CardContent className={classes.cardContent}>
        <Table>
          <TableBody>
            <DetailRow
              name={t('INSURANCE_VIEW.PRICE')}
              value={formatCurrency(insurance.price)}
            />
            <DetailRow
              name={t('INSURANCE_VIEW.IVA')}
              value={formatPercentage(insurance.iva)}
            />
            <DetailRow
              name={t('INSURANCE_VIEW.PRICE_WITH_IVA')}
              value={formatCurrency(insurance.priceWithIva)}
            />
            {(insurance.policyPdfUrl || insurance.policyXmlUrl) && (
              <>
                <DetailRow
                  name={t('INSURANCE_VIEW.POLICY_PDF')}
                  value={<DocumentLink link={insurance.policyPdfUrl} />}
                />
                <DetailRow
                  name={t('INSURANCE_VIEW.POLICY_XML')}
                  value={<DocumentLink link={insurance.policyXmlUrl} />}
                />
              </>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );

  return (
    <Box mt={3}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <PolicyInformationCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <FinancialInformationCard />
        </Grid>
      </Grid>
    </Box>
  );
};

GeneralView.propTypes = {
  insurance: PropTypes.shape({
    insuranceStatus: PropTypes.string,
    policyId: PropTypes.string,
    insurer: PropTypes.shape({
      id: PropTypes.string
    }),
    agencyId: PropTypes.string,
    coverageStart: PropTypes.string,
    coverageEnd: PropTypes.string,
    price: PropTypes.number,
    iva: PropTypes.number,
    priceWithIva: PropTypes.number,
    policyPdfUrl: PropTypes.string,
    policyXmlUrl: PropTypes.string
  })
};

export default GeneralView;
